/**
 * UnmatchedPaymentsPage — Lista niezidentyfikowanych wpłat.
 *
 * @see AC-B4, US-2 in docs/specifications/2026-05-20-unified-payment-ledger/spec.md
 *
 * Route: /admin/billing/payments?matchStatus=UNMATCHED
 *
 * Features:
 *  - Tabela z kolumnami: receivedAt, source (badge), payerEmail, kwota, externalReference (truncated + tooltip), akcja.
 *  - Default filter: receivedAfter = 30 days ago (R5, L-16).
 *  - Paginacja.
 *  - Empty state (H-20): <EmptyState title="Brak niezidentyfikowanych wpłat" />
 *  - Loading state (M-UX5): spinner + skeleton rows.
 *  - Modal "Przypnij" (AttachPaymentModal): triggerRef pattern (focus return WCAG 2.4.3).
 */
import React, { useState, useRef } from "react";
import Layout from "../../../components/Layout";
import { EmptyState } from "../../../components/common/EmptyState";
import { useUnmatchedPaymentsQuery } from "../../../hooks/useAdminProductCatalog";
import { AttachPaymentModal } from "./components/AttachPaymentModal";
import { formatDate, formatCents } from "./utils/orderFormatters";
import type { UnmatchedPaymentDto } from "../../../types/adminBilling";

// =============================================================================
// CONSTANTS
// =============================================================================

const PAGE_LIMIT = 20;
/** Milliseconds in one day — used for default `receivedAfter` window. */
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** Default lookback window for unmatched payments list (R5, L-16). */
const LOOKBACK_DAYS = 30;
/** External reference column truncation threshold (LOW Code-Quality). */
const EXTERNAL_REF_DISPLAY_MAX_LENGTH = 20;

/** Default receivedAfter: 30 days ago (R5, L-16) */
function defaultReceivedAfter(): string {
  return new Date(Date.now() - LOOKBACK_DAYS * MS_PER_DAY).toISOString();
}

// =============================================================================
// SUBCOMPONENTS
// =============================================================================

function SourceBadge({ source }: { source: string }) {
  if (source === "STRIPE") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        STRIPE
      </span>
    );
  }
  if (source === "BANK_TRANSFER") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        BANK_TRANSFER
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700">
      {source}
    </span>
  );
}

/** Truncated external reference with full value as tooltip.
 *  `title` is unreliable on touch devices — `aria-label` is added as a
 *  fallback so the full value is exposed to assistive tech regardless of
 *  hover support (UX LOW). */
function ExternalRefCell({ value }: { value?: string | null }) {
  if (!value) return <span className="text-neutral-400">—</span>;
  const truncated =
    value.length > EXTERNAL_REF_DISPLAY_MAX_LENGTH
      ? `${value.slice(0, EXTERNAL_REF_DISPLAY_MAX_LENGTH - 3)}...`
      : value;
  return (
    <span
      title={value}
      aria-label={value}
      className="font-mono text-xs cursor-help"
    >
      {truncated}
    </span>
  );
}

/** Column headers for the unmatched payments table.
 *  Extracted to avoid duplication between loading and rendered states
 *  (MEDIUM Code-Quality — single source of truth for column labels). */
const COLUMNS: Array<{ key: string; label: string; align: "left" | "right" }> = [
  { key: "date", label: "Data", align: "left" },
  { key: "source", label: "Źródło", align: "left" },
  { key: "email", label: "Email płatnika", align: "left" },
  { key: "amount", label: "Kwota", align: "left" },
  { key: "ref", label: "Referencja", align: "left" },
  { key: "action", label: "Akcja", align: "right" },
];

function PaymentsTableHeader() {
  return (
    <thead className="bg-neutral-50">
      <tr>
        {COLUMNS.map((col) => (
          <th
            key={col.key}
            className={`px-4 py-3 text-${col.align} text-xs font-medium text-neutral-500 uppercase`}
          >
            {col.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/** Skeleton row for loading state (M-UX5) */
function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-neutral-200 rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export const UnmatchedPaymentsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  // UX HIGH fix: allow admin to toggle between "last 30 days" (default) and "all".
  // Without this toggle a payment older than 30 days that is unmatched is invisible.
  const [showAll, setShowAll] = useState(false);
  const offset = page * PAGE_LIMIT;
  const receivedAfter = showAll ? undefined : defaultReceivedAfter();

  const [selectedPayment, setSelectedPayment] = useState<UnmatchedPaymentDto | null>(null);
  // Per-row trigger refs — needed to return focus after AttachPaymentModal closes (WCAG 2.4.3)
  const attachTriggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const activeTriggerRef = useRef<HTMLButtonElement | null>(null);

  const { data, isLoading, isError } = useUnmatchedPaymentsQuery({
    receivedAfter,
    limit: PAGE_LIMIT,
    offset,
  });

  const handleToggleFilter = () => {
    setShowAll((prev) => !prev);
    setPage(0); // Reset pagination on filter change.
  };

  const payments = data?.data ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? PAGE_LIMIT;

  const handleOpenAttach = (payment: UnmatchedPaymentDto) => {
    activeTriggerRef.current = attachTriggerRefs.current[payment.id] ?? null;
    setSelectedPayment(payment);
  };

  const handleCloseAttach = () => {
    setSelectedPayment(null);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Niezidentyfikowane wpłaty
          </h1>
          <p className="text-neutral-600 mt-1 text-sm">
            {showAll
              ? "Wszystkie wpłaty bez przypisanego zamówienia (bez limitu czasowego)."
              : "Wpłaty z ostatnich 30 dni bez przypisanego zamówienia."}
          </p>

          {/* Filter control (UX HIGH fix): visible filter state + toggle to see all. */}
          <div className="mt-3 flex items-center gap-3">
            <span
              data-testid="unmatched-filter-badge"
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                showAll
                  ? "bg-amber-100 text-amber-800"
                  : "bg-neutral-100 text-neutral-700"
              }`}
            >
              {showAll ? "Filtr: wszystkie (bez limitu)" : "Filtr: ostatnie 30 dni"}
            </span>
            <button
              type="button"
              onClick={handleToggleFilter}
              className="text-xs text-primary-700 hover:text-primary-900 underline"
            >
              {showAll ? "Pokaż tylko ostatnie 30 dni" : "Pokaż wszystkie"}
            </button>
          </div>
        </div>

        {/* Content */}
        {isError ? (
          <div className="p-6 text-center">
            <p className="text-red-600">Wystąpił błąd podczas ładowania danych.</p>
          </div>
        ) : isLoading ? (
          <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
            <table className="w-full text-sm">
              <PaymentsTableHeader />
              <tbody className="divide-y divide-neutral-200">
                {/* Loading state — M-UX5 */}
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </tbody>
            </table>
          </div>
        ) : payments.length === 0 ? (
          <div className="bg-white rounded-lg border border-neutral-200">
            <EmptyState
              title="Brak niezidentyfikowanych wpłat"
              description={
                showAll
                  ? "Brak wpłat bez przypisanego zamówienia."
                  : "Wszystkie wpłaty z ostatnich 30 dni zostały przypisane do zamówień."
              }
            />
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
            <table className="w-full text-sm">
              <PaymentsTableHeader />
              <tbody className="bg-white divide-y divide-neutral-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 text-xs text-neutral-700">
                      {formatDate(payment.receivedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <SourceBadge source={payment.source} />
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-700">
                      {payment.payerEmail ?? (
                        <span className="text-neutral-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-neutral-800">
                      {formatCents(payment.amountCents)}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <ExternalRefCell value={payment.externalReference} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        ref={(el) => {
                          attachTriggerRefs.current[payment.id] = el;
                        }}
                        type="button"
                        onClick={() => handleOpenAttach(payment)}
                        className="px-3 py-1 text-xs border border-primary-300 text-primary-700 rounded-md hover:bg-primary-50"
                      >
                        Przypnij do zamówienia
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 text-sm text-neutral-600">
                <span>
                  Wyświetlanie {offset + 1}–{Math.min(offset + limit, total)} z {total}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={offset === 0}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Poprzednia
                  </button>
                  <button
                    disabled={offset + limit >= total}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Następna
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AttachPaymentModal */}
      {selectedPayment && (
        <AttachPaymentModal
          open={true}
          payment={selectedPayment}
          onClose={handleCloseAttach}
          triggerRef={{ current: activeTriggerRef.current }}
        />
      )}
    </Layout>
  );
};

export default UnmatchedPaymentsPage;
